import {
  DashboardState,
  EAppSupportType,
  EProjectType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  IConditionModel,
  IIfThenElseDataModel,
  ISymbol,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import type { CascaderOptionType, CascaderValueType } from "antd/es/cascader";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { IActionClipboard } from "../../../../models/interface/action-clipboard";
import ActionService from "../../../../services/action-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { ActionDefinitionLookup } from "../../../../shared/utility/action-definition-lookup";
import { rebindDesignerPastedAction } from "../../../../shared/utility/designer-action-clipboard-actions";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { isOfflineCompatibleActionDefinition } from "../../../../shared/utility/offline-action-compatibility";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { ActionPanel, EActionPanelStatus } from "./action-panel";
import { EActionNodeCategory } from "./action-picker";
import type { IActionPickerOption } from "./action-picker-popup/action-picker-node";
import ActionPickerPopup from "./action-picker-popup/action-picker-popup";

export interface IIfThenElseSymbolGroupState {
  popupVisibleIf: boolean;
  popupVisibleElse: boolean;
  /** The option tree of whichever popup is open, built when it opens. */
  options: IActionPickerOption[];
}

export interface IIfThenElseSymbolGroupProps {
  ifThenElseData?: IIfThenElseDataModel;
  updateIfThenElseData: (ifThenElseData: IIfThenElseDataModel, changeReason?: ESdChangeReason) => void;
  getActionDefinitionItems: () => CascaderOptionType[];
}

class IfThenElseSymbolGroup extends PureSdComponent<
  IIfThenElseSymbolGroupProps & IWithSDProps & IWithContextProp,
  IIfThenElseSymbolGroupState
> {
  constructor(props: IIfThenElseSymbolGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      popupVisibleIf: false,
      popupVisibleElse: false,
      options: []
    };
  }

  /** Opens and closes the IF popup. The tree is built on open, so it lists the current catalogue. */
  handleIfPopupOpenChange = (open: boolean): void => {
    if (open) {
      this.setState({ options: this.props.getActionDefinitionItems(), popupVisibleIf: true });
    } else if (this.state.popupVisibleIf) {
      this.setState({ popupVisibleIf: false });
    }
  };

  /** Opens and closes the ELSE popup. The tree is built on open, so it lists the current catalogue. */
  handleElsePopupOpenChange = (open: boolean): void => {
    if (open) {
      this.setState({ options: this.props.getActionDefinitionItems(), popupVisibleElse: true });
    } else if (this.state.popupVisibleElse) {
      this.setState({ popupVisibleElse: false });
    }
  };

  /** The popup's pick: the same `selectedOptions` path the Cascader's onChange delivered. */
  handlePickIf = (selectedOptions: CascaderOptionType[]): void => {
    const values: CascaderValueType = selectedOptions.map((option: CascaderOptionType) => option.value ?? "");
    this.handleAddIfAction(values, selectedOptions);
  };

  handlePickElse = (selectedOptions: CascaderOptionType[]): void => {
    const values: CascaderValueType = selectedOptions.map((option: CascaderOptionType) => option.value ?? "");
    this.handleAddElseAction(values, selectedOptions);
  };

  actionDefinitionLookup: ActionDefinitionLookup = new ActionDefinitionLookup();

  getActionDefinitionById = (id: string): IActionDefinitionDto | undefined =>
    this.actionDefinitionLookup.get(this.props.sd.actionDefinitions, id);

  shouldActionsBeVisible = (): boolean => {
    if (_.isUndefined(this.props.ifThenElseData?.condition?.field1)) return false;
    if (
      _.isUndefined(this.props.ifThenElseData?.condition?.field2) &&
      this.props.ifThenElseData?.condition?.conditionOperator != "isEmpty" &&
      this.props.ifThenElseData?.condition?.conditionOperator != "isNotEmpty" &&
      this.props.ifThenElseData?.condition?.conditionOperator != "isGuid"
    )
      return false;
    if (_.isEmpty(this.props.ifThenElseData?.condition.field1)) return false;
    if (
      _.isEmpty(this.props.ifThenElseData?.condition.field2) &&
      this.props.ifThenElseData?.condition?.conditionOperator != "isEmpty" &&
      this.props.ifThenElseData?.condition?.conditionOperator != "isNotEmpty" &&
      this.props.ifThenElseData?.condition?.conditionOperator != "isGuid"
    )
      return false;

    return true;
  };

  handleAdd = (
    additionType: "if" | "else",
    value: CascaderValueType,
    selectedOptions?: CascaderOptionType[] | undefined
  ) => {
    if (!selectedOptions || selectedOptions.length == 0) {
      return;
    }
    const node = selectedOptions.find(
      (o) => o.category === EActionNodeCategory.action || o.category === EActionNodeCategory.triggerEventAction
    );

    if (node === undefined) {
      return;
    }
    let actions = this.props.sd.actionDefinitions;
    const isWebScreen = this.props.sd.getIsWebScreen();
    actions = actions.filter((x) => {
      const { projectType } = this.props.sd.project;
      if (
        x.appSupportType &&
        x.appSupportType === EAppSupportType.Web &&
        projectType !== EProjectType.Desktop &&
        this.props.sd.dashboardState !== DashboardState.scheduledJobs &&
        !isWebScreen
      ) {
        return false;
      }
      if (
        x.appSupportType &&
        x.appSupportType === EAppSupportType.Mobile &&
        projectType === EProjectType.Desktop &&
        this.props.sd.dashboardState !== DashboardState.scheduledJobs
      ) {
        return false;
      }
      if (this.props.sd.project.offlineScreenId === KMainFunctions.getSdIdFromUrl()) {
        if (isOfflineCompatibleActionDefinition(x)) return true;
        return false;
      }
      return true;
    });
    let action: IAction | undefined;

    switch (node.category) {
      case EActionNodeCategory.action:
        action = this.obtainAction(actions, node);
        break;
      case EActionNodeCategory.triggerEventAction:
        action = this.obtainTriggerAction(actions, selectedOptions);
        break;
    }

    if (!action) return;

    if (additionType === "if") {
      this.createIfAction(action);
    } else {
      this.createElseAction(action);
    }
  };

  obtainAction(actions: IActionDefinitionDto[], node: CascaderOptionType): IAction | undefined {
    const actionDef = actions.find((p) => p.id === node.value);
    if (!actionDef) return;

    return {
      id: Guid.create().toString(),
      actionDefinitionId: actionDef.id,
      label: actionDef.name,
      description: actionDef.name,
      parameters: actionDef.actionParameters.map((x) => this.cloneActionParameter(x))
    } as IAction;
  }

  obtainTriggerAction(actions: IActionDefinitionDto[], selectedOptions: CascaderOptionType[]): IAction | undefined {
    const actionDef = actions.find((p) => p.name === "TriggerEvent");
    if (!actionDef) return;

    const targetDiNode = selectedOptions[1];
    const targetEventNode = selectedOptions[2];
    const targetDiIdActionParam = actionDef.actionParameters.find((x) => x.name === "targetDiId");
    const targetEventNameActionParam = actionDef.actionParameters.find((x) => x.name === "targetEventName");

    if (!targetDiIdActionParam || !targetEventNameActionParam) return;

    targetDiIdActionParam.symbol = {
      category: ESymbolCategory.fixed,
      fixedValue: targetDiNode.value
    } as ISymbol;

    targetEventNameActionParam.symbol = {
      category: ESymbolCategory.fixed,
      fixedValue: targetEventNode.value
    } as ISymbol;

    return {
      id: Guid.create().toString(),
      actionDefinitionId: actionDef.id,
      label: actionDef.name,
      description: `Trigger [diId:${targetDiNode.value}] > ${targetEventNode.label}`,
      parameters: [targetDiIdActionParam, targetEventNameActionParam]
    } as IAction;
  }

  handleAddIfAction = (value: CascaderValueType, selectedOptions?: CascaderOptionType[] | undefined) => {
    this.handleAdd("if", value, selectedOptions);
  };

  handleAddElseAction = (value: CascaderValueType, selectedOptions?: CascaderOptionType[] | undefined) => {
    this.handleAdd("else", value, selectedOptions);
  };

  handlePasteAction = (branchType: "if" | "else") => {
    const clipboardValue = this.props.context.getClipboardValue("action") as IActionClipboard | undefined;
    if (!clipboardValue || !clipboardValue.action) return;

    const sd = this.props.sd.getScreenDesign();
    const diToAdd = clipboardValue.navigationDI;
    const copyAction = _.cloneDeep(clipboardValue.action);

    ActionService.setNewIdsForAction(copyAction);
    rebindDesignerPastedAction(copyAction, this.props.sd, clipboardValue);

    if (diToAdd) {
      const newDI = ScreenDesignHelper.renewDisplayItem(diToAdd, this.props.sd.screenDesignDictionary);
      newDI.parentId = SpecialDIIds.rootDI;

      if (newDI.friendlyName) {
        newDI.friendlyName = ScreenDesignHelper.incrementFriendlyName(newDI.friendlyName);
      }

      copyAction.parameters.forEach((param) => {
        if (param.name === "navigationKuikaId") {
          param.symbol.fixedValue = newDI.id;
        }
      });

      const rootDiIdx = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);
      if (rootDiIdx >= 0) {
        sd.displayItems[rootDiIdx].children.push(newDI);
      }
    }

    if (branchType === "if") {
      this.createIfAction(copyAction);
    } else {
      this.createElseAction(copyAction);
    }

    if (diToAdd) {
      this.props.sd.setScreenDesign(sd, ESdChangeReason.DiActionUpdated);
    }
  };

  cloneActionParameter = (actionParameter: IActionParameter) => {
    const tmp = _.cloneDeep(actionParameter);
    return tmp;
  };

  getEmptyIfThenElseData = (): IIfThenElseDataModel => {
    return {
      ifActions: [],
      elseActions: [],
      condition: {} as IConditionModel
    } as IIfThenElseDataModel;
  };

  createIfAction = (action: IAction) => {
    let ifThenElseData = _.cloneDeep(this.props.ifThenElseData);
    if (ifThenElseData === undefined) {
      ifThenElseData = this.getEmptyIfThenElseData();
    }
    ifThenElseData.ifActions.push(action);
    this.props.updateIfThenElseData(ifThenElseData);
  };

  createElseAction = (action: IAction) => {
    let ifThenElseData = _.cloneDeep(this.props.ifThenElseData);
    if (ifThenElseData === undefined) {
      ifThenElseData = this.getEmptyIfThenElseData();
    }
    ifThenElseData.elseActions.push(action);
    this.props.updateIfThenElseData(ifThenElseData);
  };

  updateActionFromIfActions = (action: IAction, changeReason?: ESdChangeReason) => {
    let ifThenElseData = _.cloneDeep(this.props.ifThenElseData);
    if (ifThenElseData === undefined) {
      ifThenElseData = this.getEmptyIfThenElseData();
    }
    ifThenElseData.ifActions = ifThenElseData?.ifActions?.map((x) => {
      if (x.id === action.id) {
        return action;
      }
      return { ...x };
    });
    this.props.updateIfThenElseData(ifThenElseData, changeReason);
  };

  deleteActionFromIfActions = (action: IAction) => {
    let ifThenElseData = _.cloneDeep(this.props.ifThenElseData);
    if (ifThenElseData === undefined) {
      ifThenElseData = this.getEmptyIfThenElseData();
    }
    ifThenElseData.ifActions = ifThenElseData.ifActions.filter((x) => x.id !== action.id);
    this.props.updateIfThenElseData(ifThenElseData);
  };

  updateActionFromElseActions = (action: IAction) => {
    let ifThenElseData = _.cloneDeep(this.props.ifThenElseData);
    if (ifThenElseData === undefined) {
      ifThenElseData = this.getEmptyIfThenElseData();
    }
    ifThenElseData.elseActions = ifThenElseData?.elseActions?.map((x) => {
      if (x.id === action.id) {
        return action;
      }
      return { ...x };
    });
    this.props.updateIfThenElseData(ifThenElseData);
  };

  deleteActionFromElseActions = (action: IAction) => {
    let ifThenElseData = _.cloneDeep(this.props.ifThenElseData);
    if (ifThenElseData === undefined) {
      ifThenElseData = this.getEmptyIfThenElseData();
    }
    ifThenElseData.elseActions = ifThenElseData.elseActions.filter((x) => x.id !== action.id);
    this.props.updateIfThenElseData(ifThenElseData);
  };

  reorderEventActions = (result: any, sourceIdx: number, destIdx: number, ifOrElse: string) => {
    let ifThenElseData = _.cloneDeep(this.props.ifThenElseData);
    if (ifThenElseData === undefined) {
      ifThenElseData = this.getEmptyIfThenElseData();
    }
    if (ifOrElse === "if") {
      const [removed] = ifThenElseData.ifActions.splice(sourceIdx, 1);
      ifThenElseData.ifActions.splice(destIdx, 0, removed);
    } else {
      const [removed] = ifThenElseData.elseActions.splice(sourceIdx, 1);
      ifThenElseData.elseActions.splice(destIdx, 0, removed);
    }
    this.props.updateIfThenElseData(ifThenElseData);
  };

  getSelectedDisplayItem = () => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    return selectedDisplayItem;
  };

  getActionDefinition = (actionDefinitionId: string | undefined): IActionDefinitionDto | undefined => {
    if (!actionDefinitionId) return undefined;
    return this.props.sd.actionDefinitions.find((x) => x.id === actionDefinitionId);
  };

  getActionDefinitions = (actions: IAction[]): Map<string, IActionDefinitionDto> => {
    const actionDefinitions: Map<string, IActionDefinitionDto> = new Map();

    actions.forEach((action) => {
      const actionDefinition = this.getActionDefinition(action.actionDefinitionId);
      if (actionDefinition && action.actionDefinitionId) {
        actionDefinitions.set(action.actionDefinitionId, actionDefinition);
      }
    });

    return actionDefinitions;
  };

  updateActionList = (actionListType: "if" | "else", currentActionIdx: number, targetActionIdx: number) => {
    let ifThenElseData = _.cloneDeep(this.props.ifThenElseData);
    if (ifThenElseData === undefined) return;

    if (actionListType === "if") {
      const [removed] = ifThenElseData.ifActions.splice(currentActionIdx, 1);
      ifThenElseData.ifActions.splice(targetActionIdx, 0, removed);
    } else {
      const [removed] = ifThenElseData.elseActions.splice(currentActionIdx, 1);
      ifThenElseData.elseActions.splice(targetActionIdx, 0, removed);
    }
    this.props.updateIfThenElseData(ifThenElseData);
  };

  render() {
    const clipboardValue = this.props.context.getClipboardValue("action") as IActionClipboard | undefined;
    const isPasteVisible = !!clipboardValue;
    const actionDefinitions = this.getActionDefinitions([
      ...(this.props.ifThenElseData?.ifActions ?? []),
      ...(this.props.ifThenElseData?.elseActions ?? [])
    ]);
    return (
      <div
        style={{
          marginTop: 12,
          marginBottom: 12,
          backgroundColor: "var(--dark-0)"
        }}
      >
        <div className="kuika_symbol-picker__if-else">
          <div className="kuika-right-panel__row">
            <div className="kuika-right-panel__col kuika-right-panel__col--label">
              <span
                style={{
                  paddingLeft: 6,
                  paddingTop: 10,
                  display: "block",
                  fontWeight: 700,
                  lineHeight: "normal",
                  color: "var(--dark-9)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                IF Actions
              </span>
            </div>
            <div className="kuika-right-panel__col kuika-right-panel__col--control" style={{ textAlign: "right" }}>
              <div>
                <ActionPickerPopup
                  projectType={this.props.sd.project?.projectType}
                  options={this.state.options}
                  open={this.state.popupVisibleIf}
                  onOpenChange={this.handleIfPopupOpenChange}
                  onPick={this.handlePickIf}
                  getActionDefinition={this.getActionDefinitionById}
                  searchTestId="ifThenElseActionAddIfActionInput"
                  placeholder="Search any action"
                >
                  <span
                    data-testid="ifThenElseActionAddIfButton"
                    className="kuika_addNavigationLink"
                    style={{ paddingRight: 6 }}
                  >
                    + ADD IF
                  </span>
                </ActionPickerPopup>
              </div>
              {isPasteVisible && !this.state.popupVisibleIf && (
                <a
                  className="kuika_addNavigationLink"
                  data-testid="ifThenElseActionPasteIfButton"
                  style={{ paddingRight: 6, cursor: "pointer" }}
                  onClick={() => this.handlePasteAction("if")}
                >
                  + PASTE
                </a>
              )}
            </div>
          </div>
          <DragDropContext
            onDragEnd={(result) => {
              if (result.destination?.index !== null && result.destination?.index !== undefined) {
                this.reorderEventActions(result, result.source.index, result.destination.index, "if");
              }
            }}
          >
            <Droppable droppableId={"droppableIfActions"}>
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} data-testid="ifThenElseIfActionList">
                  {this.props.ifThenElseData?.ifActions.map((action, idx) => {
                    const actionsLength = this.props.ifThenElseData?.ifActions.length;

                    return (
                      <Draggable key={`draggableIf_${action.id}`} draggableId={`draggableIf_${action.id}`} index={idx}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            data-testid="actionRow"
                          >
                            <ActionPanel
                              eventName="if"
                              onActionSaved={this.updateActionFromIfActions}
                              actionPanelStatus={EActionPanelStatus.edit}
                              action={_.cloneDeep(action)}
                              actionDefinition={actionDefinitions.get(action.actionDefinitionId!)}
                              deleteAction={this.deleteActionFromIfActions}
                              getActionDefinitionItems={this.props.getActionDefinitionItems}
                              isDragging={snapshot.isDragging}
                              moveActionUp={idx > 0 ? () => this.updateActionList("if", idx, idx - 1) : undefined}
                              moveActionDown={
                                idx < (actionsLength ?? 0) - 1
                                  ? () => this.updateActionList("if", idx, idx + 1)
                                  : undefined
                              }
                            />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        <div className="kuika_symbol-picker__if-else" style={{ marginTop: 12 }}>
          <div className="kuika-right-panel__row">
            <div className="kuika-right-panel__col kuika-right-panel__col--label">
              <span
                style={{
                  paddingLeft: 6,
                  paddingTop: 10,
                  display: "block",
                  fontWeight: 700,
                  lineHeight: "normal",
                  color: "var(--dark-9)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                ELSE Actions
              </span>
            </div>
            <div className="kuika-right-panel__col kuika-right-panel__col--control" style={{ textAlign: "right" }}>
              <div>
                <ActionPickerPopup
                  projectType={this.props.sd.project?.projectType}
                  options={this.state.options}
                  open={this.state.popupVisibleElse}
                  onOpenChange={this.handleElsePopupOpenChange}
                  onPick={this.handlePickElse}
                  getActionDefinition={this.getActionDefinitionById}
                  searchTestId="ifThenElseActionAddElseActionInput"
                  placeholder="Search any action"
                >
                  <span
                    data-testid="ifThenElseActionAddElseButton"
                    className="kuika_addNavigationLink"
                    style={{ paddingRight: 6 }}
                  >
                    + ADD ELSE
                  </span>
                </ActionPickerPopup>
              </div>
              {isPasteVisible && !this.state.popupVisibleElse && (
                <a
                  className="kuika_addNavigationLink"
                  data-testid="ifThenElseActionPasteElseButton"
                  style={{ paddingRight: 6, cursor: "pointer" }}
                  onClick={() => this.handlePasteAction("else")}
                >
                  + PASTE
                </a>
              )}
            </div>
          </div>
          <DragDropContext
            onDragEnd={(result) => {
              if (result.destination?.index !== null && result.destination?.index !== undefined) {
                this.reorderEventActions(result, result.source.index, result.destination.index, "else");
              }
            }}
          >
            <Droppable droppableId={"droppableElseActions"}>
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} data-testid="ifThenElseElseActionList">
                  {this.props.ifThenElseData?.elseActions.map((action, idx) => {
                    const actionsLength = this.props.ifThenElseData?.elseActions.length;

                    return (
                      <Draggable
                        key={`draggableElse_${action.id}`}
                        draggableId={`draggableElse_${action.id}`}
                        index={idx}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            data-testid="actionRow"
                          >
                            <ActionPanel
                              eventName="else"
                              onActionSaved={this.updateActionFromElseActions}
                              actionPanelStatus={EActionPanelStatus.edit}
                              action={_.cloneDeep(action)}
                              actionDefinition={actionDefinitions.get(action.actionDefinitionId!)}
                              deleteAction={this.deleteActionFromElseActions}
                              getActionDefinitionItems={this.props.getActionDefinitionItems}
                              isDragging={snapshot.isDragging}
                              moveActionUp={idx > 0 ? () => this.updateActionList("else", idx, idx - 1) : undefined}
                              moveActionDown={
                                idx < (actionsLength ?? 0) - 1
                                  ? () => this.updateActionList("else", idx, idx + 1)
                                  : undefined
                              }
                            />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.WFStepMappingChange ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ConfirmationMessageChanged ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged ||
      changeReason === ESdChangeReason.RecurringIntervalChange
    ) {
      return true;
    }
    return false;
  };
}

const ifThenElseSymbolGroup = withContext<IIfThenElseSymbolGroupProps>(
  withSD<IIfThenElseSymbolGroupProps>(IfThenElseSymbolGroup)
);
export { ifThenElseSymbolGroup as IfThenElseSymbolGroup };
